import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { DeviceOrientationEnum, FilesService, MeetingsService, ScreenService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';

import { intervalToDuration } from 'date-fns';
import { ImageSizeEnum, Meeting } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCoverModule, OnUiPopoverModule } from 'onclass-ui';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';

@Component({
  selector: 'app-meeting-footer-details',
  templateUrl: './meeting-footer-details.component.html',
  styleUrls: ['../../styles/footer-details.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, OnUiCoverModule, OnUiPopoverModule],
})
export class MeetingFooterDetailsComponent extends ChangableComponent {
  // DI
  private screenService = inject(ScreenService);
  public deviceService = inject(DeviceDetectorService);

  @Input() public set meeting(value: Meeting) {
    if (value) {
      this._meeting = value;
    }
    this.detectChanges();
  }

  public get meeting() {
    return this._meeting;
  }

  public _meeting: Meeting;

  public screenClasses() {
    const isPortrait = this.screenService.deviceOrientation === DeviceOrientationEnum.portrait;
    return {
      tablet: this.deviceService.isTablet(),
      mobile: this.deviceService.isMobile(),
      desktop: (!this.deviceService.isTablet() && !this.deviceService.isMobile()) || this.deviceService.isDesktop(),
      portrait: !this.deviceService.isDesktop() && isPortrait,
      landscape: !this.deviceService.isDesktop() && !isPortrait,
    };
  }

  public get type() {
    return MeetingsService.meetingTypeName(this.meeting);
  }

  public get cover(): OnUiCover {
    return {
      img:
        FilesService.getFileBySize(this.meeting.cover)?.url ||
        FilesService.getFileUrlBySize(this.meeting.cover_id, ImageSizeEnum.md) ||
        this.assetsUrl('/assets/onclass-logo.svg'),
      title: this.title,
    };
  }

  public get title() {
    return this.meeting?.title;
  }

  public get subjectName() {
    return this.meeting?.subject?.title;
  }

  public get levelName(): string {
    const level = this.meeting?.level?.code;
    return level ? 'subject-levels.' + level : null;
  }

  public get duration() {
    if (this.meeting.duration) {
      return this.meeting.duration;
    } else {
      const begin = new Date(this.meeting.begin_at);
      const end = new Date(this.meeting.end_at);
      return intervalToDuration({ start: begin.getTime(), end: end.getTime() }).minutes;
    }
  }
}
